// One way to call the API: the bearer token (or the staff key), X-Tenant-Id when staff act on a tenant, and
// problem details turned into typed errors.
import { getConfig } from "../config";

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
  reason?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails) {
    super(problem.title || problem.detail || `The API answered ${status}.`);
    this.status = status;
    this.problem = problem;
  }

  /** The field errors of a 400, keyed by field. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [field, messages] of Object.entries(this.problem.errors ?? {})) {
      const key = field.charAt(0).toLowerCase() + field.slice(1);
      out[key] = messages.join(" ");
    }
    return out;
  }

  get invitationRequired(): boolean {
    return this.status === 403 && this.problem.reason === "invitation-required";
  }

  /** The machine-readable reason of a problem, when the API gives one. */
  get reason(): string | null {
    return this.problem.reason ?? null;
  }
}

export type TokenProvider = () => Promise<string | null>;

export interface ApiContext {
  getToken: TokenProvider;
  staffKey?: string | null;
  tenantId?: string | null;
  tenantHeader: string;
  onUnauthorized?: () => void;
}

let context: ApiContext | null = null;

export function setApiContext(next: ApiContext) {
  context = next;
}

export function getApiContext(): ApiContext | null {
  return context;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** Bypass the staff tenant header (for /me, /admin/*). */
  noTenant?: boolean;
}

export function apiUrl(path: string, query?: RequestOptions["query"]): string {
  const base = getConfig().apiUrl;
  const url = new URL(`${base}/api/v1${path.startsWith("/") ? path : "/" + path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== null && value !== undefined && value !== "") url.searchParams.set(key, String(value));
  }
  return url.toString();
}

export async function authHeaders(noTenant = false): Promise<Record<string, string>> {
  if (!context) throw new Error("The API context is not set.");
  const headers: Record<string, string> = {};
  if (context.staffKey) {
    headers["X-Staff-Key"] = context.staffKey;
  } else {
    const token = await context.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  if (!noTenant && context.tenantId) headers[context.tenantHeader] = context.tenantId;
  return headers;
}

/**
 * How long a read may take. A read that never answers would otherwise hold its query for good: the query's poll
 * waits on the read already in flight rather than starting another, so the screen stays stale until a reload.
 */
const READ_TIMEOUT_MS = 30_000;

/** Calls the API and returns the JSON body (or null for 204). Throws ApiError for problem responses. */
export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = await authHeaders(options.noTenant);
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  const method = options.method ?? "GET";
  // Reads only: a write cut off here could still finish on the server.
  const timeout = method === "GET" ? AbortSignal.timeout(READ_TIMEOUT_MS) : null;
  const signal = timeout && options.signal ? AbortSignal.any([options.signal, timeout]) : timeout ?? options.signal;
  let response: Response;
  let text: string;
  try {
    response = await fetch(apiUrl(path, options.query), { method, headers, body, signal });
    if (response.status === 204) return null as T;
    text = await response.text();
  } catch (e) {
    if (timeout?.aborted && !options.signal?.aborted) throw new Error("AutoVouch took too long to answer. Try again in a moment.");
    throw e;
  }
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { title: text };
    }
  }
  if (!response.ok) {
    if (response.status === 401) context?.onUnauthorized?.();
    throw new ApiError(response.status, (parsed as ProblemDetails) ?? { title: response.statusText });
  }
  return parsed as T;
}

/**
 * Posts a form with the same headers through XMLHttpRequest, which fetch can't do: it reports how much of the
 * body has gone so an upload can show progress, and an AbortSignal cancels it.
 */
export async function apiUpload<T = unknown>(path: string, form: FormData, onProgress: (loaded: number, total: number) => void, signal?: AbortSignal): Promise<T> {
  const headers = await authHeaders();
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", apiUrl(path));
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onload = () => {
      let parsed: unknown = null;
      if (xhr.responseText) {
        try {
          parsed = JSON.parse(xhr.responseText);
        } catch {
          parsed = { title: xhr.responseText };
        }
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(parsed as T);
      } else {
        if (xhr.status === 401) context?.onUnauthorized?.();
        reject(new ApiError(xhr.status, (parsed as ProblemDetails) ?? { title: xhr.statusText }));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, { title: "The upload didn’t reach the API." }));
    xhr.onabort = () => reject(new DOMException("The upload was cancelled.", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(form);
  });
}

/** Fetches a file (PDF, CSV, zip) with the same headers; returns the blob and the server's file name. */
export async function apiBlob(path: string, options: RequestOptions = {}): Promise<{ blob: Blob; fileName: string | null }> {
  const headers = await authHeaders(options.noTenant);
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  const response = await fetch(apiUrl(path, options.query), { method: options.method ?? "GET", headers, body, signal: options.signal });
  if (!response.ok) {
    let problem: ProblemDetails = { title: response.statusText };
    try {
      problem = (await response.json()) as ProblemDetails;
    } catch {
      /* no body */
    }
    throw new ApiError(response.status, problem);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return { blob: await response.blob(), fileName: match ? decodeURIComponent(match[1]) : null };
}

export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = Object.values(error.fieldErrors);
    return fields.length ? fields.join(" ") : error.problem.title || error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}
